/**
 * Views rail of the Findings queue: the six system views with live
 * counts, then saved views (personal and shared). Each entry is a link,
 * so views are URL-addressable and open in a new tab.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal, Plus } from "lucide-react";
import type { FindingQueueQuery, QueueSystemView } from "@iamu/shared";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ScrollFade } from "@/components/ui/scroll-fade";

export const SYSTEM_VIEW_LABEL: Record<QueueSystemView, string> = {
  triage: "Unreviewed",
  fix_next: "Ready to fix",
  awaiting_rerun: "Awaiting re-run",
  regressed: "Regressed",
  snoozed: "Snoozed",
  closed: "Dismissed",
};

const SYSTEM_ORDER: QueueSystemView[] = [
  "triage",
  "fix_next",
  "awaiting_rerun",
  "closed",
];

interface QueueRailProps {
  projectId: string;
  counts: Partial<Record<QueueSystemView, number>> | undefined;
  activeView: QueueSystemView | null;
  activeSavedId: string | null;
  /** Current query, offered by "Save view". */
  currentQuery: FindingQueueQuery;
  canEditShared: boolean;
}

const railLink = (active: boolean) =>
  cn(
    "flex min-h-9 shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-md px-2.5 text-sm outline-none transition-colors",
    "hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
    active
      ? "bg-muted font-semibold text-foreground"
      : "text-muted-foreground hover:text-foreground",
  );

export function QueueRail({
  projectId,
  counts,
  activeView,
  activeSavedId,
  currentQuery,
  canEditShared,
}: QueueRailProps) {
  const base = `/projects/${projectId}/findings`;
  const [saving, setSaving] = useState(false);
  const utils = trpc.useUtils();
  const { data: views } = trpc.findingView.list.useQuery({ projectId });
  const refresh = () => utils.findingView.list.invalidate({ projectId });
  const remove = trpc.findingView.delete.useMutation({
    onSuccess: () => {
      refresh();
      toast.success("View deleted");
    },
    onError: (e) => toastTrpcError(e, "Failed to delete view"),
  });
  const reorder = trpc.findingView.reorder.useMutation({
    onSuccess: refresh,
    onError: (e) => toastTrpcError(e, "Failed to reorder views"),
  });

  function move(id: string, by: -1 | 1) {
    const ids = (views ?? []).map((v) => v.id);
    const i = ids.indexOf(id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate({ projectId, ids });
  }

  return (
    <nav aria-label="Finding views" className="space-y-4">
      <ScrollFade
        as="ul"
        className="-mx-4 flex gap-1.5 px-4 pb-2 scrollbar-none lg:m-0 lg:block lg:space-y-0.5 lg:overflow-visible lg:p-0"
      >
        {SYSTEM_ORDER.map((v) => (
          <li key={v}>
            <Link
              to={`${base}?view=${v}`}
              className={railLink(activeView === v)}
              aria-current={activeView === v ? "page" : undefined}
            >
              <span>{SYSTEM_VIEW_LABEL[v]}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {counts?.[v] ?? ""}
              </span>
            </Link>
          </li>
        ))}
      </ScrollFade>

      <div className="space-y-1">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Saved views
          </h2>
          <SimpleTooltip content="Save current view">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Save the current view"
              onClick={() => setSaving(true)}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </Button>
          </SimpleTooltip>
        </div>
        {(views ?? []).length === 0 ? (
          <p className="px-2 text-xs text-muted-foreground">
            Filter the queue, then save it here.
          </p>
        ) : (
          <ScrollFade
            as="ul"
            className="-mx-4 flex gap-1.5 px-4 pb-2 scrollbar-none lg:m-0 lg:block lg:space-y-0.5 lg:overflow-visible lg:p-0"
          >
            {(views ?? []).map((v, i) => (
              <li key={v.id} className="flex items-center gap-1">
                <Link
                  to={`${base}?view=${v.id}`}
                  className={cn(railLink(activeSavedId === v.id), "flex-1")}
                  aria-current={activeSavedId === v.id ? "page" : undefined}
                >
                  <span className="truncate">{v.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {v.shared ? "Shared" : "Mine"}
                  </span>
                </Link>
                {v.canEdit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={`Options for view ${v.name}`}
                      >
                        <MoreHorizontal
                          className="h-4 w-4"
                          aria-hidden="true"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={i === 0}
                        onClick={() => move(v.id, -1)}
                      >
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={i === (views ?? []).length - 1}
                        onClick={() => move(v.id, 1)}
                      >
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => remove.mutate({ id: v.id })}
                      >
                        Delete view
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </li>
            ))}
          </ScrollFade>
        )}
      </div>

      {saving && (
        <SaveViewDialog
          projectId={projectId}
          query={currentQuery}
          canShare={canEditShared}
          onClose={() => setSaving(false)}
          onSaved={refresh}
        />
      )}
    </nav>
  );
}

function SaveViewDialog({
  projectId,
  query,
  canShare,
  onClose,
  onSaved,
}: {
  projectId: string;
  query: FindingQueueQuery;
  canShare: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const create = trpc.findingView.create.useMutation({
    onSuccess: () => {
      onSaved();
      toast.success(shared ? "Shared view saved" : "View saved");
      onClose();
    },
    onError: (e) => toastTrpcError(e, "Failed to save view"),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save view</DialogTitle>
          <DialogDescription>
            Keeps the current filters, grouping and sort in the rail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="save-view-name">Name</Label>
            <Input
              id="save-view-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="save-view-shared"
              checked={shared}
              disabled={!canShare}
              onCheckedChange={(v) => setShared(v === true)}
            />
            <Label htmlFor="save-view-shared" className="font-normal">
              Share with the project
              {!canShare ? " (editors and owners only)" : ""}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate({ projectId, name: name.trim(), shared, query })
            }
          >
            Save view
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
